import { env } from '@/config/env';
import { supabase } from '@/lib/supabaseClient';
import type { Checkout } from '@/platform/types';

/**
 * Пакеты кредитов и создание счёта (ADR-047).
 *
 * Клиент не считает ни цену, ни число кредитов: пакеты читаются из базы под
 * RLS (только активные), а счёт создаёт сервер по id пакета. Что оплата
 * дошла и кредиты начислены, узнаётся только по балансу.
 */
export interface CreditPack {
  id: string;
  label: string;
  credits: number;
  priceStars: number;
}

export class PaymentsError extends Error {
  readonly code: string;

  constructor(message: string, code: string) {
    super(message);
    this.name = 'PaymentsError';
    this.code = code;
  }
}

const UNEXPECTED = 'Сервер ответил неожиданно. Попробуйте ещё раз.';

export async function fetchPacks(): Promise<CreditPack[]> {
  if (!supabase) {
    return [];
  }

  const { data, error } = await supabase
    .from('credit_packs')
    .select('id, label, credits, price_stars, sort')
    .eq('active', true)
    .not('price_stars', 'is', null)
    .order('sort');

  if (error) {
    throw new PaymentsError('Не удалось загрузить пакеты. Попробуйте ещё раз.', 'UNKNOWN_ERROR');
  }

  return (data ?? []).flatMap((row) =>
    row.price_stars === null
      ? []
      : [{ id: row.id, label: row.label, credits: row.credits, priceStars: row.price_stars }],
  );
}

export async function createCheckout(accessToken: string, packId: string): Promise<Checkout> {
  let response: Response;

  try {
    response = await fetch(`${env.VITE_SUPABASE_URL}/functions/v1/payments-create`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        apikey: env.VITE_SUPABASE_ANON_KEY,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ packId, provider: 'telegram_stars' }),
    });
  } catch {
    throw new PaymentsError('Нет связи с сервером. Проверьте интернет.', 'NETWORK_ERROR');
  }

  const payload = (await response.json().catch(() => null)) as
    | { checkout?: { kind?: unknown; invoiceUrl?: unknown }; error?: { code?: unknown; message?: unknown } }
    | null;

  if (!payload) {
    throw new PaymentsError(UNEXPECTED, 'UNKNOWN_ERROR');
  }

  if (!response.ok || payload.error) {
    throw new PaymentsError(
      typeof payload.error?.message === 'string' ? payload.error.message : 'Не удалось открыть оплату. Попробуйте ещё раз.',
      typeof payload.error?.code === 'string' ? payload.error.code : 'UNKNOWN_ERROR',
    );
  }

  const checkout = payload.checkout;

  if (checkout?.kind !== 'telegram-stars' || typeof checkout.invoiceUrl !== 'string') {
    throw new PaymentsError(UNEXPECTED, 'UNKNOWN_ERROR');
  }

  return { kind: 'telegram-stars', invoiceUrl: checkout.invoiceUrl };
}
